import {
  UpdateElementText,
  UpdateElementFormattedText,
  AddElementAttribute,
  ShowElement,
  HideElement,
  UpdateElement,
  UpdateOrderUnConfirmed,
  UpdateShippingFee,
  UpdateShippingFeeSupport,
  UpdateFreezingFeeSupport,
  ToggleElementsByType,
  UpdateOrderIsConfirmed,
} from "./types";

/**
 * 要素の存在確認をし、存在した場合要素の更新を行う
 * @param existsElement 要素の存在確認を行う関数
 * @param setTextToElement 指定した要素のテキストを書き換える関数
 * @param selector 指定した要素
 */
export const updateElementText: UpdateElementText = (
  existsElement,
  setTextToElement,
  selector
) => (text) => {
  if (existsElement(selector)) {
    setTextToElement(selector, text);
  }
};

/**
 * 要素の存在確認をし、存在した場合要素の値をフォーマットして更新を行う
 * @param existsElement 要素の存在確認を行う関数
 * @param setTextToElement 指定した要素のテキストを書き換える関数
 * @param formattedPrice 3桁区切りでカンマを付与した価格を返す関数
 * @param selector 指定した要素
 */
export const updateElementFormattedText: UpdateElementFormattedText = (
  existsElement,
  setTextToElement,
  formattedPrice,
  selector
) => (price) => {
  if (existsElement(selector)) {
    setTextToElement(selector, formattedPrice(price));
  }
};

/**
 * 要素の存在確認をし、存在した場合要素を表示にする
 * @param existsElement 要素の存在確認を行う関数
 * @param removeAttribute 属性を削除する関数
 * @param selector 指定した要素
 */
export const showElement: ShowElement = (
  existsElement,
  removeAttribute,
  selector
) => () => {
  if (existsElement(selector)) {
    removeAttribute(selector, "hidden");
  }
};

/**
 * 要素の存在確認をし、存在した場合要素を非表示にする
 * @param existsElement 要素の存在確認を行う関数
 * @param setAttribute 指定した要素のテキストを書き換える関数
 * @param selector 指定した要素
 */
export const hideElement: HideElement = (
  existsElement,
  setAttribute,
  selector
) => () => {
  if (existsElement(selector)) {
    setAttribute(selector, "hidden", "true");
  }
};

/**
 * 要素の存在確認をし、存在した場合指定した属性を追加する
 * @param existsElement 要素の存在確認を行う関数
 * @param setAttribute 指定した要素のテキストを書き換える関数
 * @param selector 指定した要素
 */
export const addElementAttribute: AddElementAttribute = (
  existsElement,
  setAttribute,
  selector
) => (name, value) => {
  if (existsElement(selector)) {
    setAttribute(selector, name, value);
  }
};

/**
 * 受け取る値によって、指定した要素の表示の切り替えと値の更新を行う
 * @param existsElement 要素の存在確認を行う関数
 * @param existsChildElement 指定した要素の子要素の存在確認を行う関数
 * @param setTextToElement 指定した要素のテキストを書き換える関数
 * @param formattedPrice 3桁区切りでカンマを付与した価格を返す関数
 * @param setAttribute 属性を設定する関数
 * @param removeAttribute 属性を削除する関数
 * @param selector 指定した要素
 * @param childSelector 指定した子要素
 */
export const updateElement: UpdateElement = (
  existsElement,
  existsChildElement,
  setTextToElement,
  formattedPrice,
  setAttribute,
  removeAttribute,
  selector,
  childSelector
) => (value) => {
  if (!existsElement(selector) || !existsChildElement(selector, childSelector))
    return;
  if (!value) {
    setAttribute(selector, "hidden", "true");
  } else {
    removeAttribute(selector, "hidden");
    setTextToElement(childSelector, formattedPrice(value));
  }
};

/**
 * 注文未確定の表示切り分けを行う
 * @param existsElement 要素の存在確認を行う関数
 * @param setAttribute 属性を設定する関数
 * @param removeAttribute 属性を削除する関数
 * @param selector 指定した要素
 */
export const updateOrderUnConfirmed: UpdateOrderUnConfirmed = (
  existsElement,
  setAttribute,
  removeAttribute,
  selector
) => (hasOrderConfirmed) => {
  if (!existsElement(selector)) return;
  hasOrderConfirmed
    ? setAttribute(selector, "hidden", "true")
    : removeAttribute(selector, "hidden");
};

/**
 * 送料表示の切り替えと値の更新を行う
 * @param existsElement 要素の存在確認を行う関数
 * @param setAttribute 属性を設定する関数
 * @param removeAttribute 属性を削除する関数
 * @param selector 指定した要素
 */
export const updateShippingFee: UpdateShippingFee = (
  existsElement,
  setTextToElement,
  setAttribute,
  removeAttribute,
  selector
) => (text, isShown) => {
  if (!existsElement(selector)) return;
  setTextToElement(selector, text);
  isShown
    ? removeAttribute(selector, "hidden")
    : setAttribute(selector, "hidden", "true");
};

/**
 * 送料サポートの切り替えと値の更新を行う
 * @param existsElement 要素の存在確認を行う関数
 * @param setTextToElement 指定した要素のテキストを書き換える関数
 * @param formattedPrice 3桁区切りでカンマを付与した価格を返す関数
 * @param setAttribute 属性を設定する関数
 * @param removeAttribute 属性を削除する関数
 * @param getTextContent テキストを取得する関数
 * @param selector 指定した要素
 * @param discountNeedSelector 割引必要金額を表示する要素
 * @param discountPriceSelector 割引金額を表示する要素
 * @param discountEstSelector 割引お得金額を表示する要素
 * @param replicationSelector 送料サポートの表記を複製する要素
 */
export const updateShippingFeeSupport: UpdateShippingFeeSupport = (
  existsElement,
  setTextToElement,
  formattedPrice,
  setAttribute,
  removeAttribute,
  getTextContent,
  selector,
  discountNeedSelector,
  discountPriceSelector,
  discountEstSelector,
  replicationSelector
) => (text, isShown, discountNeed, discountDiff) => {
  if (!existsElement(selector)) return;
  if (isShown) {
    removeAttribute(selector, "hidden");
    if (existsElement(discountNeedSelector))
      setTextToElement(discountNeedSelector, formattedPrice(discountNeed));
    if (existsElement(discountPriceSelector))
      setTextToElement(discountPriceSelector, text);
    if (existsElement(discountEstSelector))
      setTextToElement(discountEstSelector, formattedPrice(discountDiff));
    if (existsElement(replicationSelector))
      setTextToElement(replicationSelector, getTextContent(selector));
  } else {
    setAttribute(selector, "hidden", "true");
  }
};

/**
 * 冷凍手数料サポートの切り替えと値の更新を行う
 * @param existsElement 要素の存在確認を行う関数
 * @param setTextToElement 指定した要素のテキストを書き換える関数
 * @param formattedPrice 3桁区切りでカンマを付与した価格を返す関数
 * @param setAttribute 属性を設定する関数
 * @param removeAttribute 属性を削除する関数
 * @param getTextContent テキストを取得する関数
 * @param selector 指定した要素
 * @param freezingNeedPriceSelector 割引必要金額（冷凍品）を表示する要素
 * @param totalNeedPriceSelector 割引必要金額（総額）を表示する要素
 * @param replicationSelector 送料サポートの表記を複製する要素
 */
export const updateFreezingFeeSupport: UpdateFreezingFeeSupport = (
  existsElement,
  setTextToElement,
  formattedPrice,
  setAttribute,
  removeAttribute,
  getTextContent,
  selector,
  freezingNeedPriceSelector,
  totalNeedPriceSelector,
  replicationSelector
) => (isShown, freezingNeedPrice, totalNeedPrice) => {
  if (!existsElement(selector)) return;
  if (isShown) {
    removeAttribute(selector, "hidden");
    if (existsElement(freezingNeedPriceSelector))
      setTextToElement(
        freezingNeedPriceSelector,
        formattedPrice(freezingNeedPrice)
      );
    if (existsElement(totalNeedPriceSelector))
      setTextToElement(totalNeedPriceSelector, formattedPrice(totalNeedPrice));
    if (existsElement(replicationSelector))
      setTextToElement(replicationSelector, getTextContent(selector));
  } else {
    setAttribute(selector, "hidden", "true");
  }
};

/**
 * 受け取ったタイプによって、指定した要素らの表示切り替えを行う
 * @param existsElement 要素の存在確認を行う関数
 * @param setAttribute 属性を設定する関数
 * @param removeAttribute 属性を削除する関数
 * @param selector 指定した要素
 * @param alternativeSelector 代替要素
 */
export const toggleElementsByType: ToggleElementsByType = (
  existsElement,
  setAttribute,
  removeAttribute,
  selector,
  alternativeSelector
) => (type) => {
  if (!existsElement(selector) || !existsElement(alternativeSelector)) return;
  switch (type) {
    case 2:
      setAttribute(alternativeSelector, "hidden", "true");
      removeAttribute(selector, "hidden");
      break;
    default:
      setAttribute(selector, "hidden", "true");
      removeAttribute(alternativeSelector, "hidden");
  }
};

/**
 * 要素の存在確認をし、存在した場合要素の値を更新する
 * @param existsElement 要素の存在確認を行う関数
 * @param setInputValue 要素の値を更新する関数
 * @param selector 指定した要素
 */
export const updateOrderIsConfirmed: UpdateOrderIsConfirmed = (
  existsElement,
  setInputValue,
  selector
) => (value) => {
  if (existsElement(selector)) {
    setInputValue(selector, value);
  }
};
